import React, { memo, useCallback, useMemo, useState } from 'react';

import { message } from 'antd';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import {
  PiChatCircleText,
  PiInfo,
  PiPencilSimpleLight,
  PiPushPin,
  PiPushPinSlash,
  PiTrash,
} from 'react-icons/pi';
import { useParams } from 'react-router-dom';
import { BottomSheet } from 'react-spring-bottom-sheet';

import { SocketAPI } from '../../../api/SocketAPI';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal/ConfirmationModal';
import {
  changeChatsState,
  setEditMessageData,
  setReplyMessageData,
  setTouchedMessageData,
  updateChatRoomDetails,
  updateOrDeleteSelectedMessage,
  updateReplyState,
} from '../../../context/communityMessageReducer';
import { useAppDispatch, useAppSelector } from '../../../shared/hooks';
import { ICommunityMessage } from '../../../types/communityMessagesTypes';
import { SOCKET_ACTION_EVENTS } from '../../../types/socketTypes';
import { UAMPermissions } from '../../../types/userTypes';

type Props = {
  setInfoMessageId: (arg0: string) => void;
};

const TouchedMode: React.FC<Props> = ({ setInfoMessageId }) => {
  const dispatch = useAppDispatch();
  const { roomId } = useParams();
  const userDetails = useAppSelector((state) => state?.user);
  const { touchedMessageData, chatRoomDetails, replayMessageData, replyState } =
    useAppSelector((state) => state?.communityMessage);

  const isPinned = useMemo(() => {
    const chat = chatRoomDetails?.pinnedMessages?.find(
      (each) => each?._id === touchedMessageData?._id,
    );
    return Boolean(chat?._id);
  }, [chatRoomDetails?.pinnedMessages, touchedMessageData?._id]);

  const isCreatorOrUAM = useMemo(() => {
    return (
      touchedMessageData?.sender?._id === userDetails?.id ||
      userDetails?.userAccessPermissions?.includes(
        UAMPermissions?.COMMUNITY_ACCESS,
      )
    );
  }, [
    touchedMessageData?.sender?._id,
    userDetails?.id,
    userDetails?.userAccessPermissions,
  ]);

  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [selecteMessageForDelete, setSelecteMessageForDelete] =
    useState<ICommunityMessage | null>(null);

  const closeModal = useCallback(() => {
    dispatch(setTouchedMessageData(null));
  }, [dispatch]);

  const handleOnEmojiClick = useCallback(
    (emojiData: EmojiClickData) => {
      if (!chatRoomDetails?._id || !touchedMessageData?._id) return;
      SocketAPI.emit(
        SOCKET_ACTION_EVENTS.ROOM_MESSAGE_REACT,
        {
          room: chatRoomDetails?._id,
          messageId: touchedMessageData?._id,
          reaction: emojiData.emoji,
        },
        (resp: any) => {
          if (resp?.status === 'success') {
            if (replayMessageData) {
              const newReplyState = replyState?.replies?.map((each) => {
                if (each?._id === touchedMessageData?._id) {
                  return resp.result;
                }
                return each;
              });
              dispatch(updateReplyState({ replies: newReplyState }));
            } else {
              dispatch(
                updateOrDeleteSelectedMessage(resp.result, false, () => {}),
              );
            }
            closeModal();
          } else if (resp?.status === 'error') {
            message.error(resp?.message);
          }
        },
      );
    },
    [
      chatRoomDetails?._id,
      touchedMessageData?._id,
      replayMessageData,
      replyState?.replies,
      dispatch,
      closeModal,
    ],
  );

  const handleReplyThread = useCallback(() => {
    dispatch(setReplyMessageData(touchedMessageData));
    closeModal();
  }, [dispatch, touchedMessageData, closeModal]);

  const afterPinnedAction = useCallback(() => {
    closeModal();
    if (!touchedMessageData) return;
    const currentPinnedMessages = chatRoomDetails?.pinnedMessages;
    let newUpdatedPinnedMessage = [...(currentPinnedMessages || [])];
    if (isPinned) {
      newUpdatedPinnedMessage = newUpdatedPinnedMessage.filter(
        (each) => each?._id !== touchedMessageData?._id,
      );
    } else {
      newUpdatedPinnedMessage = [
        touchedMessageData,
        ...newUpdatedPinnedMessage,
      ];
    }
    dispatch(
      updateChatRoomDetails({ pinnedMessages: newUpdatedPinnedMessage }),
    );
    message.success(
      isPinned
        ? 'Unpinned message successfully'
        : 'Pinned message successfully',
    );
  }, [
    closeModal,
    touchedMessageData,
    chatRoomDetails?.pinnedMessages,
    isPinned,
    dispatch,
  ]);

  const handlePinToChat = useCallback(() => {
    if (!roomId || !touchedMessageData?._id) return;
    const actionType = isPinned ? 'unpinned' : 'pinned';
    dispatch(
      changeChatsState(
        actionType,
        roomId,
        touchedMessageData?._id,
        () => {},
        () => afterPinnedAction(),
        () => {},
        {},
        isPinned ? 'action=unpin' : 'action=pin',
      ),
    );
  }, [roomId, touchedMessageData?._id, isPinned, dispatch, afterPinnedAction]);

  const handleMessageInfo = useCallback(() => {
    if (!touchedMessageData?._id) return;
    setInfoMessageId(touchedMessageData?._id);
    closeModal();
  }, [touchedMessageData?._id, setInfoMessageId, closeModal]);

  const handleEditMessage = useCallback(() => {
    dispatch(setEditMessageData(touchedMessageData));
    closeModal();
  }, [dispatch, touchedMessageData, closeModal]);

  const handleDeleteMessage = useCallback(() => {
    if (!selecteMessageForDelete?._id || !chatRoomDetails?._id) return;
    SocketAPI.emit(
      SOCKET_ACTION_EVENTS.ROOM_MESSAGE_DELETE,
      {
        room: chatRoomDetails?._id,
        messageId: selecteMessageForDelete?._id,
      },
      (resp: any) => {
        if (resp?.status === 'success') {
          if (replayMessageData) {
            const newReplyState = replyState?.replies?.filter(
              (each) => each?._id !== selecteMessageForDelete?._id,
            );
            dispatch(updateReplyState({ replies: newReplyState }));
          } else {
            dispatch(
              updateOrDeleteSelectedMessage(
                selecteMessageForDelete,
                true,
                () => {},
              ),
            );
          }
          message.success('Message deleted successfully');
        } else if (resp?.status === 'error') {
          message.error(resp?.message);
        }
      },
    );
  }, [
    selecteMessageForDelete,
    chatRoomDetails?._id,
    replayMessageData,
    replyState?.replies,
    dispatch,
  ]);

  return (
    <>
      {Boolean(touchedMessageData?._id) && (
        <BottomSheet
          open={Boolean(touchedMessageData?._id)}
          onDismiss={closeModal}
          style={{ zIndex: 0 }}>
          {null}
          <div className="touched">
            <EmojiPicker
              className="emoji_container_wrapper"
              onEmojiClick={handleOnEmojiClick}
              autoFocusSearch={false}
              reactionsDefaultOpen
              lazyLoadEmojis={false}
              onReactionClick={handleOnEmojiClick}
            />
            <div className="touched_btn-wrapper">
              {replayMessageData ? null : (
                <button
                  className="touched_btn-wrapper_button"
                  onClick={handleReplyThread}>
                  <div className="touched_btn-wrapper_button_emoji">
                    <PiChatCircleText />
                  </div>
                  Reply in thread
                </button>
              )}
              {isCreatorOrUAM ? (
                <button
                  className="touched_btn-wrapper_button"
                  onClick={handleEditMessage}>
                  <div className="touched_btn-wrapper_button_emoji">
                    <PiPencilSimpleLight />
                  </div>
                  Edit message
                </button>
              ) : null}
              <button
                className="touched_btn-wrapper_button"
                onClick={handleMessageInfo}>
                <div className="touched_btn-wrapper_button_emoji">
                  <PiInfo />
                </div>
                Message info
              </button>
              {replayMessageData ? null : (
                <button
                  className="touched_btn-wrapper_button"
                  onClick={handlePinToChat}>
                  <div className="touched_btn-wrapper_button_emoji">
                    {isPinned ? <PiPushPinSlash /> : <PiPushPin />}
                  </div>
                  {isPinned ? 'Unpin to chat' : 'Pin to chat'}
                </button>
              )}
              <button
                className="touched_btn-wrapper_button danger"
                onClick={() => {
                  setSelecteMessageForDelete(touchedMessageData);
                  setIsDialogVisible(true);
                  closeModal();
                }}>
                <div className="touched_btn-wrapper_button_emoji">
                  <PiTrash />
                </div>
                Delete message
              </button>
            </div>
          </div>
        </BottomSheet>
      )}
      <ConfirmationModal
        open={isDialogVisible}
        message="Do you really want to delete this comment?"
        title="Delete message"
        okayButtonText="Delete"
        cancelButtonText="Cancel"
        handleOk={() => {
          setIsDialogVisible(false);
          handleDeleteMessage();
        }}
        handleCancel={() => {
          setIsDialogVisible(false);
          setSelecteMessageForDelete(null);
        }}
      />
    </>
  );
};

export default memo(TouchedMode);
